import * as _ from 'lodash';
import * as moment from 'moment';
import * as DateUtils from './DateUtils';
import { ACCESS_OPTION_FUNCTION, ACCESS_OPTIONS, RESOURCE_TYPE, RESOURCES } from './role-resource-constants';
import { getUserDropDownMapping, isUserAvailableForDate } from './UtilityFunctions';

const USER_ROLE_ADMINISTRATOR = 'Administrator';
const USER_ROLE_VENDOR = 'Vendor';

function addTwentyFourHoursToTime(time) {
  return `${parseInt(time.split(':')[0], 10) + 24}:${time.split(':')[1]}`;
}

function isGivenTimeBetweenTheseTimes(givenTime, startTime, endTime) {
  if (endTime < startTime) {
    endTime = addTwentyFourHoursToTime(endTime);
    if (givenTime < startTime) {
      givenTime = addTwentyFourHoursToTime(givenTime);
    }
  }

  return givenTime <= endTime && givenTime >= startTime;
}

function getShiftTimeForUserAndDate(user, date = new Date()) {
  const currentHHMM = moment(date).format('HH:mm');
  const { shiftStartTime } = user; // shift times are in minutes, if shift time is 07:21, value would be 7*60+21 = 441
  const { shiftEndTime } = user;
  if (!_.isNumber(shiftStartTime) || !_.isNumber(shiftEndTime) || !user.timeZone) {
    console.debug(
      `${user.firstName} ${user.lastName} does not have user shift start time or shift end time or time zone`
    );
    return null;
  }
  const totalShiftTimeInMin =
    shiftEndTime > shiftStartTime ? shiftEndTime - shiftStartTime : shiftEndTime + 24 * 60 - shiftStartTime;

  const shiftStartTimeInHHMM = DateUtils.convertMinutesToHHMM(shiftStartTime); // shift time which are in just minutes will be converted into HH:mm format, e.g. 441 would be 07:21
  const shiftEndTimeInHHMM = DateUtils.convertMinutesToHHMM(shiftEndTime);

  // these shift times were in the user time zone, converting these shift times to the local times
  const localShiftStartTimeInHHMM = DateUtils.convertTimeToLocalTimeZone(shiftStartTimeInHHMM, user.timeZone);
  const localShiftEndTimeInHHMM = DateUtils.convertTimeToLocalTimeZone(shiftEndTimeInHHMM, user.timeZone);

  if (isGivenTimeBetweenTheseTimes(currentHHMM, localShiftStartTimeInHHMM, localShiftEndTimeInHHMM)) {
    // user is in shift
    const currentShift = {};
    currentShift.startDate = (
      currentHHMM > localShiftStartTimeInHHMM
        ? moment(`${moment(date).format('YYYY-MM-DD')} ${localShiftStartTimeInHHMM}`)
        : moment(`${moment(date).format('YYYY-MM-DD')} ${localShiftStartTimeInHHMM}`).subtract(1, 'day')
    ).toDate();
    currentShift.endDate = moment(currentShift.startDate).add(totalShiftTimeInMin, 'minute').toDate();
    return currentShift;
  }
  const previousShift = {};
  previousShift.endDate = (
    currentHHMM > localShiftEndTimeInHHMM
      ? moment(`${moment(date).format('YYYY-MM-DD')} ${localShiftEndTimeInHHMM}`)
      : moment(`${moment(date).format('YYYY-MM-DD')} ${localShiftEndTimeInHHMM}`).subtract(1, 'day')
  ).toDate();
  previousShift.startDate = moment(previousShift.endDate).subtract(totalShiftTimeInMin, 'minute').toDate();
  return previousShift;
}

/**
 * this function gets user's whole day(current or previous shift time + next out of shift time) not just shift time, even if user is not in shift.
 * For example, currentTime is 2019-01-23T23:10 and user's shift time is {startDate: 2019-01-23T02:00, endDate: 2019-01-23T11:00}
 * returned value would be {startDate: 2019-01-23T02:00, endDate: 2019-01-24T01:59}
 * @param user
 * @param date
 * @returns {{endDate, startDate: Date}}
 */
export function getWholeDayForAssignee(user, date = new Date()) {
  const shiftTime = getShiftTimeForUserAndDate(user, date);
  if (!shiftTime) return null;
  const dayTime = {
    startDate: shiftTime.startDate,
    endDate: moment(shiftTime.startDate).add(1, 'day').subtract(1, 'second').toDate(),
  };
  return dayTime;
}

export function isUserAdministrator(user) {
  return !!(
    user &&
    user.roles &&
    user.roles.map((role) => role.roleId).find((roleId) => roleId === USER_ROLE_ADMINISTRATOR)
  );
}

export function isUserVendor(user) {
  return !!(user && user.roles && user.roles.map((role) => role.roleId).find((roleId) => roleId === USER_ROLE_VENDOR));
}

export function getAccessOptionsForResourceAndUser({
  resource,
  resourceType,
  entityAccessType,
  user,
  accessOptionName,
}) {
  const { roleObjects } = user;
  return roleObjects.reduce((accessOptions, roleObj) => {
    const accessOptionValue = _.get(
      roleObj.access,
      `${resourceType}.${resource}.${entityAccessType}.${accessOptionName}`
    );
    if (accessOptionValue) accessOptions.push(accessOptionValue);
    return accessOptions;
  }, []);
}

export function doesTenantPackageHaveAccessToFeatureOrView(packageInfo = {}, resourceType, resource) {
  const { hideAccessTo = {} } = packageInfo;
  let resources = [];
  if (!resource) {
    return true;
  }
  if (Array.isArray(resource)) {
    resources = resource;
  } else {
    resources.push(resource);
  }

  const hiddenResources = hideAccessTo[resourceType] || [];

  if (resources && hiddenResources.length) {
    // if any resouce found to be part of hidden, then hide all that resouces
    return !resources.some((res) => {
      return hiddenResources.indexOf(res) !== -1;
    });
  }

  return true;
}

export function doesRoleHaveAccessToFeatureOrView(roleObject, resourceType, resource) {
  const { access } = roleObject;

  let resources = [];
  if (Array.isArray(resource)) {
    resources = resource;
  } else {
    resources.push(resource);
  }

  return (
    resources &&
    resources.some((res) => {
      if (resourceType === RESOURCE_TYPE.VIEW && !!_.get(access, `${resourceType}.${res}`)) {
        const accessToFieldAndAreas = _.get(access, 'fieldAndArea', {});
        switch (res) {
          case RESOURCES.VIEW.TASK_VIEW: {
            for (const areas of Object.values(accessToFieldAndAreas)) {
              if (areas.length > 0) {
                return true;
              }
            }
            return false;
          }
          case RESOURCES.VIEW.TASK_TYPE_VIEW:
          case RESOURCES.VIEW.TEAM_VIEW:
          case RESOURCES.VIEW.AREA_VIEW:
          case RESOURCES.VIEW.FORM_MANAGEMENT_VIEW:
          case RESOURCES.VIEW.USER_MANAGEMENT_VIEW:
            return !_.isEmpty(accessToFieldAndAreas);
          default:
            return !!_.get(access, `${resourceType}.${res}`);
        }
      } else if (resourceType === RESOURCE_TYPE.FEATURE && res === RESOURCES.FEATURE.TASK_LIST_VIEW) {
        let isTaskListViewAllowed = _.get(access, `${resourceType}.${res}`);
        if (_.isNil(isTaskListViewAllowed)) {
          isTaskListViewAllowed = true;
        }
        return isTaskListViewAllowed;
      }

      return !!_.get(access, `${resourceType}.${res}`);
    })
  );
}

function checkAccessCriteriaForEntity({ criteriaObject, entity, resource, loggedInUser }) {
  const accessObjectEntries = Object.entries(criteriaObject);

  return accessObjectEntries.every(([accessOption, accessOptionValue]) => {
    const accessOptionFunc = _.get(ACCESS_OPTION_FUNCTION, `${resource}.${accessOption}`);

    if (accessOptionFunc) {
      return !!entity && accessOptionFunc({ loggedInUser, entity, accessOptionValue });
    }
    return false;
  });
}

function doesRoleHaveAccessToEntity({ roleObject, resource, entityAccessType, entity, loggedInUser }) {
  const { access } = roleObject;
  const doesRoleHaveAccess = _.get(access, `${RESOURCE_TYPE.ENTITY}.${resource}.${entityAccessType}`);

  if (entity && _.isObject(doesRoleHaveAccess) && doesRoleHaveAccess.criteria) {
    return checkAccessCriteriaForEntity({
      criteriaObject: doesRoleHaveAccess.criteria,
      entity,
      resource,
      loggedInUser,
    });
  }

  return !!doesRoleHaveAccess;
}

export function determineIfUserHasFieldsDisabled({ resource, field, entityAccessType, user }) {
  const { roleObjects } = user;
  return roleObjects.every((roleObject) => {
    const entityAccessObject = _.get(roleObject.access, `${RESOURCE_TYPE.ENTITY}.${resource}.${entityAccessType}`);
    return !!entityAccessObject?.[`${ACCESS_OPTIONS.DISABLED_FIELD.name}`]?.includes(field);
  });
}

export function determineIfUserHasAccessToResource({
  resourceType,
  resource,
  entityAccessType,
  entity,
  user,
  packageName,
  packagesInfo = {},
}) {
  const { roleObjects } = user;
  switch (resourceType) {
    case RESOURCE_TYPE.VIEW:
    case RESOURCE_TYPE.FEATURE: {
      let tenantPackageAccess = true;
      if (packageName && packagesInfo && packagesInfo[packageName]) {
        tenantPackageAccess = doesTenantPackageHaveAccessToFeatureOrView(
          packagesInfo[packageName],
          resourceType,
          resource
        );
      }
      return (
        tenantPackageAccess &&
        roleObjects.some((roleObject) => doesRoleHaveAccessToFeatureOrView(roleObject, resourceType, resource))
      );
    }
    case RESOURCE_TYPE.ENTITY: {
      return roleObjects.some((roleObject) =>
        doesRoleHaveAccessToEntity({ roleObject, resource, entity, entityAccessType, loggedInUser: user })
      );
    }
    default:
      return false;
  }
}

export function determineIfUserHasAccessToEngineSetting({ resourceType, resource, resourceTobeChecked, user }) {
  const { roleObjects } = user;

  if (resourceType === RESOURCE_TYPE.FEATURE && resource === RESOURCES.FEATURE.DAILY_SETTINGS_OPTONS) {
    return roleObjects.some((roleObject) => {
      const dailyenginesettingsoptions = _.get(roleObject, `access.feature.${resource}`);

      if (!_.isEmpty(dailyenginesettingsoptions) && _.isArray(dailyenginesettingsoptions)) {
        return dailyenginesettingsoptions.includes(resourceTobeChecked);
      }
      return false;
    });
  }
  return false;
}

export function getFieldsAndAreasAccessibleToUser(user) {
  const accessToFieldAndAreaForEveryRoleObjects =
    user?.roleObjects?.map((ro) => _.get(ro, 'access.fieldAndArea', {})) || [];
  let wholeAccessToFieldAndArea = {};
  const mergeCustomizer = (objValue, srcValue) => {
    if (_.isArray(objValue)) {
      return _.union(objValue, srcValue);
    }
    return undefined;
  };
  for (const accessToFieldAndArea of accessToFieldAndAreaForEveryRoleObjects) {
    wholeAccessToFieldAndArea = _.mergeWith(wholeAccessToFieldAndArea, accessToFieldAndArea, mergeCustomizer);
  }
  return wholeAccessToFieldAndArea;
}

export function concatUsers(users) {
  let resultString = '';
  if (!users) {
    return '-';
  }
  users.forEach((user) => {
    if (resultString) {
      resultString += ', ';
    }
    resultString += `${user.firstName} ${user.lastName}`;
  });
  return resultString;
}

export function concatUsersByRolesAndFilterByFieldId(users = {}, selectedField) {
  let result;
  users.teamleads = _.get(users, 'teamleads', []);
  users.teammembers = _.get(users, 'teammembers', []);
  result = _.unionBy(users.teamleads, users.teammembers, 'id');
  const fieldId = selectedField.id;
  result = result.filter((user) => user.fieldId === fieldId);

  result = result.map((user) => {
    return getUserDropDownMapping(user);
  });
  return result;
}

export function getDisplayLabelIfUserIsAvailable(user) {
  const currentHHMM = moment().format('HH:mm');
  let displayLabel = null;
  const { shiftStartTime, shiftEndTime, timeZone } = user;
  if (!_.isNumber(shiftStartTime) || !_.isNumber(shiftEndTime) || !timeZone) {
    displayLabel = 'No time available';
  } else {
    const localShiftStartTimeInHHMM = DateUtils.convertTimeToLocalTimeZone(
      DateUtils.convertMinutesToHHMM(shiftStartTime),
      user.timeZone
    );
    const localShiftEndTimeInHHMM = DateUtils.convertTimeToLocalTimeZone(
      DateUtils.convertMinutesToHHMM(shiftEndTime),
      user.timeZone
    );
    if (
      currentHHMM < localShiftStartTimeInHHMM &&
      (localShiftEndTimeInHHMM > localShiftStartTimeInHHMM || currentHHMM > localShiftEndTimeInHHMM)
    ) {
      // user is yet to start the shift
      displayLabel = `${localShiftStartTimeInHHMM} - ${localShiftEndTimeInHHMM}`;
    } else if (
      (currentHHMM >= localShiftStartTimeInHHMM &&
        (currentHHMM < localShiftEndTimeInHHMM || localShiftStartTimeInHHMM > localShiftEndTimeInHHMM)) ||
      (currentHHMM < localShiftEndTimeInHHMM && localShiftStartTimeInHHMM > localShiftEndTimeInHHMM)
    ) {
      // user is in between the shift
      let minutesLeftToday = null;
      if (currentHHMM < localShiftEndTimeInHHMM) {
        minutesLeftToday = moment({
          hours: localShiftEndTimeInHHMM.split(':')[0],
          minutes: localShiftEndTimeInHHMM.split(':')[1],
        }).diff(moment(), 'minutes');
      } else {
        minutesLeftToday = moment({
          hours: localShiftEndTimeInHHMM.split(':')[0],
          minutes: localShiftEndTimeInHHMM.split(':')[1],
        })
          .add(1, 'days')
          .diff(moment(), 'minutes');
      }
      displayLabel = DateUtils.convertMinuteToHMDisplay(minutesLeftToday);
      displayLabel += ' left';
    } else {
      displayLabel = 'No time available';
    }
  }
  return displayLabel;
}

export function getLocalShiftTimings(user) {
  let displayLabel = null;
  const { shiftStartTime, shiftEndTime, timeZone } = user;
  if (!_.isNumber(shiftStartTime) || !_.isNumber(shiftEndTime) || !timeZone) {
    displayLabel = 'No time available';
  } else {
    const localShiftStartTimeInHHMM = DateUtils.convertTimeToLocalTimeZone(
      DateUtils.convertMinutesToHHMM(shiftStartTime),
      user.timeZone
    );
    const localShiftEndTimeInHHMM = DateUtils.convertTimeToLocalTimeZone(
      DateUtils.convertMinutesToHHMM(shiftEndTime),
      user.timeZone
    );
    displayLabel = `${localShiftStartTimeInHHMM} - ${localShiftEndTimeInHHMM}`;
  }
  return displayLabel;
}

export function getAvailabilityLabelForUserAndSelectedDate(assignee, selectedDate) {
  const isUserAvailableForSelectedDate = isUserAvailableForDate(selectedDate, assignee);
  let displayLabel = null;
  if (isUserAvailableForSelectedDate) {
    displayLabel =
      moment().format('YYYY-MM-DD') === selectedDate
        ? getDisplayLabelIfUserIsAvailable(assignee)
        : getLocalShiftTimings(assignee);
  } else {
    displayLabel = 'Not Available';
  }
  return displayLabel;
}
